/**
 * Straight-line isochrone approximation (M8-B) — the documented, clearly-labeled
 * stand-in the OSS default returns until a real routing engine (Valhalla/OSRM)
 * is configured at deploy time. A real isochrone follows the road network; this
 * converts each time/distance band into a radius and returns a geodesic circle
 * (`approximate: true`). Honest by construction: the tool surfaces the
 * approximation, and swapping in a road-network provider is a one-place change.
 */

import { circleRing, MILES_TO_METERS, type LngLat } from "./geo";
import { DEFAULT_ROUTE_SPEED_KMH, type IsochroneBand, type Isochrone } from "./types";

/** Convert a band to a radius (meters): miles as-is, minutes × assumed speed. */
export function bandRadiusMeters(band: IsochroneBand, speedKmh = DEFAULT_ROUTE_SPEED_KMH): number {
  if (typeof band.miles === "number" && band.miles > 0) return band.miles * MILES_TO_METERS;
  if (typeof band.minutes === "number" && band.minutes > 0) {
    const mps = (speedKmh * 1000) / 3600;
    return band.minutes * 60 * mps;
  }
  return 0;
}

/**
 * Approximate isochrones for `origin` at each band as radius-buffer circles.
 * Every returned ring is flagged `approximate: true`. Zero/negative bands are
 * dropped so a nonsense band never yields a degenerate ring.
 */
export function approxIsochrones(
  origin: LngLat,
  bands: IsochroneBand[],
  opts?: { speedKmh?: number; steps?: number },
): Isochrone[] {
  const speedKmh = opts?.speedKmh ?? DEFAULT_ROUTE_SPEED_KMH;
  const out: Isochrone[] = [];
  for (const band of bands) {
    const radius = bandRadiusMeters(band, speedKmh);
    if (radius <= 0) continue;
    out.push({ band, polygon: circleRing(origin, radius, opts?.steps), approximate: true });
  }
  return out;
}
